import { BoxConsumableStatus } from "@modules/boxConsumable/entities/boxConsumableType"
import { BoxSubscriptionStatus } from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { word } from "@modules/core/utils/i18n"
import { colors } from "@modules/ui/styles/colors"
import dayjs from "dayjs"

export const LAST_VISITED_YELLOW = 8 // 8 days
export const LAST_VISITED_RED = 15 // 15 days

export type MemberDisplayKey =
  | "firstname"
  | "lastname"
  | "name"
  | "present"
  | "inscriptionDate"
  | "status"
  | "activeOffer"
  | "lastVisit"
  | "assignedCoach"
  | "note"
  | "noshow"

/**
 * Converts the last visited date to a number and the number of days since the last visit.
 * The number it's used to define the color of the camembert. Rf: HuCambert
 * @param {date} lastVisited The last visited date.
 * @returns {number, number} An object containing the converted number and the number of days since the last visit.
 */
export const convertLasVisitedToNumberAndDays = (lastVisited: Date) => {
  const today = dayjs()
  const lastVisitedDate = dayjs(lastVisited)
  const daysSinceLastVisited = today.diff(lastVisitedDate, "day")

  if (daysSinceLastVisited > LAST_VISITED_RED) {
    return { number: 1, daysSinceLastVisited }
  } else if (daysSinceLastVisited > LAST_VISITED_YELLOW) {
    return { number: 2, daysSinceLastVisited }
  } else {
    return { number: 3, daysSinceLastVisited }
  }
}

/**
 * Converts the last visited date to a corresponding color based on the number of days since the last visit.
 * @param lastVisited The last visited date.
 * @returns The color corresponding to the number of days since the last visit.
 */
export const convertLastVisitedToColor = (lastVisited?: Date) => {
  if (lastVisited == null) {
    return ""
  }
  const today = dayjs()
  const lastVisitedDate = dayjs(lastVisited)
  const daysSinceLastVisited = today.diff(lastVisitedDate, "day")

  if (daysSinceLastVisited > LAST_VISITED_RED) {
    return "border-b-2 border-red-500"
  } else if (daysSinceLastVisited > LAST_VISITED_YELLOW) {
    return "border-b-2 border-yellow-500"
  } else {
    return "border-b-2 border-green-500"
  }
}

export const getOfferPurchaseStatusInfos = (
  status: BoxConsumableStatus | BoxSubscriptionStatus,
  count: number | null,
) => {
  switch (status) {
    case BoxSubscriptionStatus.ACTIVE:
      return { color: colors.green500, label: word("member.form.memberships.status.active.label") }
    case BoxSubscriptionStatus.INACTIVE:
      return {
        color: colors.red500,
        label: word("member.form.memberships.status.inactive.label"),
      }
    case BoxSubscriptionStatus.PENDING_CANCELLATION:
      return { color: colors.yellow500, label: word("member.form.memberships.status.pending_cancellation.label") }
    case BoxSubscriptionStatus.PENDING_PAYMENT:
      return { color: colors.yellow500, label: word("member.form.memberships.status.pending_payment.label") }
    case BoxConsumableStatus.PAID:
      if (count != null) {
        if (count < 1) {
          return {
            color: colors.red500,
            label: word("member.form.memberships.status.sold_out.label"),
          }
        } else {
          return {
            color: colors.green500,
            label: `${word("member.form.memberships.balance.label", { count: count ?? 1 })}`,
          }
        }
      }

      return {
        color: colors.green500,
        label: word("member.form.memberships.status.active.label"),
      }
    case BoxSubscriptionStatus.SCHEDULED_ACTIVATION:
      return { color: colors.yellow500, label: word("member.form.memberships.status.scheduled_activation.label") }
    default:
      return { color: colors.neutral400, label: "" }
  }
}
